import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { FeatureCollection } from 'geojson'
import { RoadCoverageProperties } from '../models/road-coverage.model'

@Injectable()
export class DataRoadCoverageService {
  private roadCoverage: FeatureCollection<GeoJSON.LineString, RoadCoverageProperties> = {
    type: 'FeatureCollection',
    features: [],
  }
  public isDataInitialized = false

  // Add request cancellation mechanism
  private cancelPreviousRequest$ = new Subject<void>()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async loadRoadCoverageData(
    isHistoryEnabled: boolean,
    bounds: maplibregl.LngLatBounds,
    timestamp?: Date,
  ) {
    // Cancel any previous ongoing request
    this.cancelPreviousRequest$.next()

    this.isDataInitialized = true

    const roadCoverage =
      isHistoryEnabled && timestamp
        ? await this.loadRoadCoverageForHistory(timestamp, bounds)
        : await this.loadRoadCoverage(bounds)

    // backend returns coverage/way_id as strings, normalize to the typed model
    roadCoverage.features.forEach((feature) => {
      feature.properties.id = crypto.randomUUID()
      feature.properties.coverage = Number(feature.properties.coverage)
      feature.properties.way_id = Number(feature.properties.way_id)
    })

    this.roadCoverage.features = roadCoverage.features

    return this
  }

  async loadRoadCoverage(
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<GeoJSON.LineString, RoadCoverageProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }
    const url = new URL(`${this.config.backend_url}vzo/road-coverage`)
    url.searchParams.set('minLng', bounds.getWest().toString())
    url.searchParams.set('minLat', bounds.getSouth().toString())
    url.searchParams.set('maxLng', bounds.getEast().toString())
    url.searchParams.set('maxLat', bounds.getNorth().toString())

    try {
      // Add takeUntil to cancel the request when a new one starts
      const result = await firstValueFrom(
        this.http
          .get<FeatureCollection<GeoJSON.LineString, RoadCoverageProperties>>(
            url.toString(),
            httpOptions,
          )
          .pipe(takeUntil(this.cancelPreviousRequest$)),
        { defaultValue: { type: 'FeatureCollection' as const, features: [] } },
      )

      return result
    } catch (error) {
      console.error('Error loading road coverage data:', error)
      return {
        type: 'FeatureCollection',
        features: [],
      }
    }
  }

  async loadRoadCoverageForHistory(
    timestamp: Date,
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<GeoJSON.LineString, RoadCoverageProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
      params: {
        timestamp: timestamp.toISOString(),
        minLat: bounds.getSouth().toString(),
        maxLat: bounds.getNorth().toString(),
        minLng: bounds.getWest().toString(),
        maxLng: bounds.getEast().toString(),
      },
    }
    const url = new URL(`${this.config.backend_url}vzo/road-coverage/historic`)

    try {
      const result = await firstValueFrom(
        this.http
          .get<FeatureCollection<GeoJSON.LineString, RoadCoverageProperties>>(
            url.toString(),
            httpOptions,
          )
          .pipe(takeUntil(this.cancelPreviousRequest$)),
        { defaultValue: { type: 'FeatureCollection' as const, features: [] } },
      )
      return result
    } catch (error) {
      console.error('Error loading road coverage history data:', error)
      return {
        type: 'FeatureCollection',
        features: [],
      }
    }
  }

  getRoadCoverage(): FeatureCollection<GeoJSON.LineString, RoadCoverageProperties> {
    return this.roadCoverage
  }

  // Method to manually cancel current requests (if needed by external services)
  public cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  // Clean up on service destruction
  ngOnDestroy(): void {
    this.cancelPreviousRequest$.next()
    this.cancelPreviousRequest$.complete()
  }
}
